import { Check } from "lucide-react";
import { useState } from "react";
import { useOutletContext } from "react-router-dom";

import {
  Button,
  ErrorBanner,
  FormSection,
  InfoBanner,
  Input,
  PageTitle,
  Select,
  Spinner,
  Switch,
} from "@/components/ui";
import WeekHours from "@/components/WeekHours";
import { toApiError } from "@/lib/api";
import { diasAbertos, semanaDeDias } from "@/lib/horarios";
import { useT } from "@/lib/i18n";
import {
  useSettings,
  useSettingsUpdate,
  useWorkingHours,
  useWorkingHoursUpdate,
} from "@/lib/queries";
import type { GarageSettings, Me, WorkingHoursWeek } from "@/lib/types";

export default function Settings() {
  const t = useT();
  const me = useOutletContext<Me>();
  const { data, isLoading, isError } = useSettings();

  if (isLoading) {
    return (
      <div className="flex min-h-[60dvh] items-center justify-center">
        <Spinner className="size-8" />
      </div>
    );
  }
  if (isError || !data) {
    return (
      <div className="p-4 lg:p-8">
        <ErrorBanner message={t("settings.loadFailed")} />
      </div>
    );
  }

  // key: trocar de oficina precisa remontar o formulário com os valores
  // da casa nova, não manter o rascunho da anterior (igual a "Meu site").
  return (
    <Formulario key={me.garage.slug} inicial={data} podeEditar={me.permissions.is_admin} />
  );
}

/** Campos numéricos vivem como texto no formulário.
 *
 * Guardar como número obriga a converter a cada tecla, e apagar o conteúdo
 * para digitar outro valor viraria 0 na tela — o dono vê "0 dias" no meio
 * de uma edição que ele nem terminou. A conversão acontece no envio.
 */
const NUMERICOS = [
  "min_advance_minutes",
  "max_advance_days",
  "cancellation_window_hours",
  "reschedule_window_hours",
] as const;

type Numerico = (typeof NUMERICOS)[number];
type Campos = Omit<GarageSettings, Numerico> & Record<Numerico, string>;

function paraFormulario(s: GarageSettings): Campos {
  return {
    ...s,
    min_advance_minutes: String(s.min_advance_minutes),
    max_advance_days: String(s.max_advance_days),
    cancellation_window_hours: String(s.cancellation_window_hours),
    reschedule_window_hours: String(s.reschedule_window_hours),
  };
}

function Formulario({
  inicial,
  podeEditar,
}: {
  inicial: GarageSettings;
  podeEditar: boolean;
}) {
  const t = useT();
  const { region } = useOutletContext<Me>().garage;
  const salvar = useSettingsUpdate();
  const [campos, setCampos] = useState<Campos>(() => paraFormulario(inicial));
  const [salvou, setSalvou] = useState(false);

  const set = <K extends keyof Campos>(chave: K, valor: Campos[K]) => {
    setCampos((c) => ({ ...c, [chave]: valor }));
    setSalvou(false);
  };

  const erro = salvar.isError ? toApiError(salvar.error) : null;

  /** Props comuns de campo numérico, com o erro do backend no lugar certo. */
  const numero = (chave: Numerico) => ({
    type: "number",
    inputMode: "numeric" as const,
    value: campos[chave],
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => set(chave, e.target.value),
    error: erro?.field === chave ? erro.message : undefined,
    disabled: !podeEditar,
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    salvar.mutate(
      {
        slot_granularity_minutes: campos.slot_granularity_minutes,
        min_advance_minutes: Number(campos.min_advance_minutes),
        max_advance_days: Number(campos.max_advance_days),
        cancellation_window_hours: Number(campos.cancellation_window_hours),
        reschedule_window_hours: Number(campos.reschedule_window_hours),
        allow_same_day_booking: campos.allow_same_day_booking,
        auto_confirm: campos.auto_confirm,
        overbooking_allowed: campos.overbooking_allowed,
        professionals_see_all_agendas: campos.professionals_see_all_agendas,
        professionals_can_overbook_self: campos.professionals_can_overbook_self,
        send_reminder_24h: campos.send_reminder_24h,
        send_reminder_2h: campos.send_reminder_2h,
      },
      {
        onSuccess: (novo) => {
          setCampos(paraFormulario(novo));
          setSalvou(true);
        },
      },
    );
  };

  return (
    <div className="p-4 lg:p-8">
      <PageTitle
        eyebrow={t("setup.eyebrow")}
        title={t("nav.scheduleRules")}
        subtitle={t("settings.subtitle")}
      />

      <div className="max-w-3xl space-y-5">
        {!podeEditar && (
          <InfoBanner tone="neutral">
            {t("settings.readOnlyHint")}
          </InfoBanner>
        )}

        <SecaoHorarios podeEditar={podeEditar} />

        <form onSubmit={submit} className="space-y-5">
          <FormSection
            title={t("settings.gridAndWindows")}
            description={t("settings.slotsHint")}
          >
            <Select
              label={t("settings.slotStep")}
              value={campos.slot_granularity_minutes}
              disabled={!podeEditar}
              onChange={(e) => set("slot_granularity_minutes", Number(e.target.value))}
            >
              {campos.granularity_choices.map((n) => (
                <option key={n} value={n}>
                  {t("settings.granularityMinutes", { n })}
                </option>
              ))}
            </Select>
            <p className="-mt-2 text-xs text-steel-dim">
              {t("settings.slotStepHint")}
            </p>

            <div className="grid gap-3 sm:grid-cols-2">
              <Input
                label={t("settings.minAdvance")}
                hint={t("settings.minAdvanceHint")}
                min={0}
                max={2880}
                {...numero("min_advance_minutes")}
              />
              <Input
                label={t("settings.bookingHorizon")}
                hint={t("settings.horizonHint")}
                min={1}
                max={365}
                {...numero("max_advance_days")}
              />
              <Input
                label={t("settings.cancelWindow")}
                hint={t("settings.cancelHint")}
                min={0}
                max={168}
                {...numero("cancellation_window_hours")}
              />
              <Input
                label={t("settings.rescheduleWindow")}
                hint={t("settings.rescheduleHint")}
                min={0}
                max={168}
                {...numero("reschedule_window_hours")}
              />
            </div>

            <Switch
              checked={campos.allow_same_day_booking}
              onChange={(v) => set("allow_same_day_booking", v)}
              label={t("settings.sameDay")}
              hint={t("settings.sameDayHint")}
            />
            <Switch
              checked={campos.auto_confirm}
              onChange={(v) => set("auto_confirm", v)}
              label={t("settings.autoConfirm")}
              hint={t("settings.autoConfirmHint")}
            />
            <Switch
              checked={campos.overbooking_allowed}
              onChange={(v) => set("overbooking_allowed", v)}
              label={t("settings.allowOverbook")}
              hint={t("settings.overbookHint")}
            />
          </FormSection>

          <FormSection
            title={t("settings.teamBehavior")}
            description={t("settings.teamBehaviorHint")}
          >
            <Switch
              checked={campos.professionals_see_all_agendas}
              onChange={(v) => set("professionals_see_all_agendas", v)}
              label={t("settings.seeAllAgendas")}
              hint={t("settings.seeAllAgendasHint")}
            />
            <Switch
              checked={campos.professionals_can_overbook_self}
              onChange={(v) => set("professionals_can_overbook_self", v)}
              label={t("settings.selfBooking")}
              hint={t("settings.selfBookingHint")}
            />
          </FormSection>

          <FormSection title={t("settings.reminders")} description={t("settings.remindersHint")}>
            <Switch
              checked={campos.send_reminder_24h}
              onChange={(v) => set("send_reminder_24h", v)}
              label={t("settings.reminder24h")}
              hint={t("settings.reminder24Hint")}
            />
            <Switch
              checked={campos.send_reminder_2h}
              onChange={(v) => set("send_reminder_2h", v)}
              label={t("settings.reminder2h")}
              hint={t("settings.reminder2Hint")}
            />
            <InfoBanner tone="neutral">
              {t("settings.reminderChannelHint", { channel: region.messaging_label })}
              {!region.messaging_available && (
                <> ({t("settings.comingSoon")})</>
              )}
            </InfoBanner>
          </FormSection>

          {erro && !erro.field && <ErrorBanner message={erro.message} />}

          {podeEditar && (
            <div className="sticky bottom-0 flex items-center gap-3 border-t border-line bg-carbon-900/95 py-4 backdrop-blur">
              <Button type="submit" variant="racing" disabled={salvar.isPending}>
                {salvar.isPending ? (
                  <Spinner className="size-4 border-carbon-900/30 border-t-carbon-900" />
                ) : (
                  t("settings.saveRules")
                )}
              </Button>
              {salvou && (
                <span className="flex items-center gap-1.5 text-sm text-emerald-400">
                  <Check size={16} /> {t("settings.savedNote")}
                </span>
              )}
            </div>
          )}
        </form>
      </div>
    </div>
  );
}

/** Horário de funcionamento — a mesma semana do assistente, editável
 *  depois que a oficina já abriu.
 *
 *  Está nesta tela porque é aqui que o dono vem quando a casa muda de
 *  rotina, e não no assistente, que ele não abre mais desde o primeiro
 *  dia. Salva no próprio botão: o endpoint é outro (`/working-hours/`
 *  troca a semana inteira), e pendurar isso no "Salvar regras" reescreveria
 *  a agenda da casa toda vez que alguém mexesse na antecedência mínima.
 */
function SecaoHorarios({ podeEditar }: { podeEditar: boolean }) {
  const t = useT();
  const { data, isLoading, isError } = useWorkingHours();

  if (isLoading) {
    return (
      <FormSection title={t("hours.title")} description={t("hours.hint")}>
        <div className="flex justify-center py-6">
          <Spinner className="size-6" />
        </div>
      </FormSection>
    );
  }
  if (isError || !data) {
    return (
      <FormSection title={t("hours.title")} description={t("hours.hint")}>
        <ErrorBanner message={t("hours.loadFailed")} />
      </FormSection>
    );
  }
  return <EditorHorarios inicial={data} podeEditar={podeEditar} />;
}

function EditorHorarios({
  inicial,
  podeEditar,
}: {
  inicial: WorkingHoursWeek;
  podeEditar: boolean;
}) {
  const t = useT();
  const salvar = useWorkingHoursUpdate();
  const [semana, setSemana] = useState(() => semanaDeDias(inicial.days));
  const [salvou, setSalvou] = useState(false);

  const abertos = diasAbertos(semana);
  const erro = salvar.isError ? toApiError(salvar.error) : null;

  /** A semana vazia fica de fora do botão, não do backend.
   *
   *  Salvar zero dias é legítimo no endpoint e catastrófico na tela: a
   *  página pública para de oferecer horário e nada explica o porquê. Quem
   *  quer fechar por um período tem bloqueio de agenda, que volta sozinho. */
  const podeSalvar = podeEditar && abertos.length > 0 && !salvar.isPending;

  return (
    <FormSection title={t("hours.title")} description={t("hours.hint")}>
      <WeekHours
        semana={semana}
        disabled={!podeEditar}
        onChange={(nova) => {
          setSemana(nova);
          setSalvou(false);
        }}
      />

      <p className="text-xs text-steel-dim">{t("hours.timeOffHint")}</p>

      {erro && <ErrorBanner message={erro.message} />}

      {podeEditar && (
        <div className="flex flex-wrap items-center gap-3">
          <Button
            type="button"
            disabled={!podeSalvar}
            onClick={() =>
              salvar.mutate(
                { unit: inicial.unit, days: abertos },
                {
                  onSuccess: (semanaSalva) => {
                    setSemana(semanaDeDias(semanaSalva.days));
                    setSalvou(true);
                  },
                },
              )
            }
          >
            {salvar.isPending ? (
              <Spinner className="size-4 border-carbon-900/30 border-t-carbon-900" />
            ) : (
              t("hours.save")
            )}
          </Button>
          {abertos.length === 0 && (
            <span className="text-xs text-amber-400">{t("hours.needOneDay")}</span>
          )}
          {salvou && (
            <span className="flex items-center gap-1.5 text-sm text-emerald-400">
              <Check size={16} /> {t("hours.saved")}
            </span>
          )}
        </div>
      )}
    </FormSection>
  );
}
